import React, { useContext, ReactNode, useCallback } from 'react';
import { UserByAuthId, UserByAuthIdVariables } from 'graphql/queries/types/UserByAuthId';
import { QuizUser } from 'graphql/queries/types';
import { getUserByAuthId } from 'graphql/queries';
import { useQuery } from '@apollo/client';
import { Navigate } from 'react-router-dom';
import { AuthRoles } from 'consts';
import { SquareLoader } from 'components';
import { User } from 'firebase/auth';

interface QuizUserContextType extends QuizUser {
    isSuperAdmin: () => boolean;
    isEventAdmin: (eventAlias?: string) => boolean;
    isQuizCreatorOrEventAdmin: (quizId: number, eventAlias?: string) => boolean;
    isEventCreatorRole: (eventAlias?: string) => boolean;
}

const UserContext = React.createContext<QuizUserContextType | null | undefined>(null);

export function useUserContext() {
    return useContext(UserContext)!;
}

export function UserProvider({ authCurrentUser, children }: { authCurrentUser: User | null, children?: ReactNode | undefined }) {
    const {
        loading,
        data
        // error,
        // refetch
    } = useQuery<UserByAuthId, UserByAuthIdVariables>(getUserByAuthId, {
        variables: { authId: authCurrentUser?.uid ?? '' },
        skip: !authCurrentUser
    });

    const isSuperAdmin = useCallback(() => {
        if (loading || !authCurrentUser || (!loading && !data?.quiz_master_users)) {
            return false;
        }

        const user = data?.quiz_master_users.at(0);

        if (user?.user_roles && user?.user_roles.length > 0 && user?.user_roles.find((ur) => ur.role_id === AuthRoles.SuperAdmin)) {
            return true;
        }

        return (
            ['dubravko.bogovic@gmail.com', 'ladabozic@gmail.com', 'dudo2k@yahoo.com'].indexOf(authCurrentUser.email!) > -1 &&
            authCurrentUser.emailVerified
        );
    }, [loading, authCurrentUser, data]);

    const isEventAdmin = useCallback((eventAlias?: string) => {
        if (loading || !authCurrentUser || (!loading && !data?.quiz_master_users)) {
            return false;
        }

        const user = data?.quiz_master_users.at(0);
        return (
            isSuperAdmin() ||
            (!!eventAlias &&
                !!user?.user_events &&
                !!user.user_events.find((ue) => ue.event.alias === eventAlias && ue.event_role_id === AuthRoles.EventAdmin))
        );
    }, [loading, authCurrentUser, data, isSuperAdmin]);

    const isQuizCreatorOrEventAdmin = useCallback(
        (quizId: number, eventAlias?: string): boolean => {
            if (loading || !authCurrentUser || (!loading && !data?.quiz_master_users)) {
                return false;
            }

            const user = data?.quiz_master_users.at(0);
            return isEventAdmin(eventAlias) || !!user?.quizzes.find((q) => q.id === quizId);
        },
        [loading, authCurrentUser, data, isEventAdmin]
    );

    const isEventCreatorRole = useCallback((eventAlias?: string) => {
        if (loading || !authCurrentUser || (!loading && !data?.quiz_master_users)) {
            return false;
        }

        const user = data?.quiz_master_users.at(0);
        return (
            isEventAdmin(eventAlias) ||
            (!!eventAlias &&
                !!user?.user_events &&
                !!user.user_events.find((ue) => ue.event.alias === eventAlias && ue.event_role_id === AuthRoles.EventCreator))
        );
    }, [loading, authCurrentUser, data, isEventAdmin]);

    if (!loading && authCurrentUser && !data?.quiz_master_users) {
        return <Navigate to="/" replace />;
    }

    const contextValue = {
        ...data?.quiz_master_users.at(0)!,
        isSuperAdmin,
        isEventAdmin,
        isQuizCreatorOrEventAdmin,
        isEventCreatorRole
    };

    return (
        <UserContext.Provider value={contextValue}>
            {!loading && children}
            {loading && <SquareLoader color='black' />}
        </UserContext.Provider>
    );
}
